/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1755561600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Aug 19 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "expedia",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Expedia Data Clean Rooms",
          "depth": 0,
          "id": "expedia-data-clean-rooms"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Connect to AWS DCR + Expedia Group Media Solutions",
          "depth": 0,
          "id": "connect-to-aws-dcr--expedia-group-media-solutions"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing conversion data",
          "depth": 1,
          "id": "syncing-conversion-data"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      pre: "pre",
      span: "span",
      h3: "h3",
      ol: "ol",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "expedia-data-clean-rooms",
        children: "Expedia Data Clean Rooms"
      }), "\n", _jsx(_components.p, {
        children: "Expedia uses AWS Clean Room technology to build secure, tailored data collaborations that let partners share insights without exposing sensitive information."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Conversion Data"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to AWS Data Clean Room to use with Expedia Group Media Solutions conversion data"
            }), _jsx(_components.td, {
              children: "Mirror"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "Mirror mode means that the current table will be cleared and replaced with all the rows in the new query result, every time the sync runs."
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "To get started, you need:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "to contact Expedia Group to set up an AWS Data Clean Room collaboration with them. This will involve sharing an AWS account ID with Expedia Group."
        }), "\n", _jsxs(_components.li, {
          children: ["AWS ", _jsx(_components.a, {
            href: "/security/aws",
            children: "credentials configured in Hightouch"
          }), " with programmatic access enabled and permission to write to the S3 path, use Glue databases and tables, and associate tables to data clean rooms."]
        }), "\n", _jsxs(_components.li, {
          children: ["the following AWS resources ", _jsxs(_components.strong, {
            children: ["(these AWS resources must reside in ", _jsx(_components.code, {
              children: "us-east-1"
            }), ", consistent with Expedia's Data Clean Room)"]
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["an ", _jsx(_components.a, {
                href: "https://docs.aws.amazon.com/AmazonS3/latest/userguide/creating-bucket.html",
                children: "S3 bucket"
              }), " to store your synced data in. ", _jsx(_components.strong, {
                children: "Make sure that this bucket is only used for your data that you are syncing to use with Expedia"
              }), "."]
            }), "\n", _jsxs(_components.li, {
              children: ["an S3 bucket for Data Clean Room analysis results. ", _jsx(_components.strong, {
                children: "This must be a different bucket than the above bucket used for storing synced data"
              }), "."]
            }), "\n", _jsx(_components.li, {
              children: "your AWS Data Clean Room collaboration membership ID with Expedia Group. This can be found in your AWS Data Clean Room collaboration with Expedia under Details > Membership Details > Membership ID after creating a collaboration with Expedia."
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure that the configured user has the following IAM permission policy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: ["{\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\":"
          }), " [\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:*\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3-object-lambda:*\""
          }), "\n            ],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), "\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sts:AssumeRole\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"iam:PassRole\""
          }), "\n            ],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:iam::${accountId}:role/${IAMRoleName}\""
          }), "\n        }\n    ]\n}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "And a separate IAM Role with the following permission policy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: ["{\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\":"
          }), " [\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"CloudWatchLogsAccess\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"logs:CreateLogGroup\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"logs:CreateLogStream\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"logs:PutLogEvents\""
          }), "\n            ],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), "\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"GlueCatalogAccess\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:CreateDatabase\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:GetDatabase\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:CreateTable\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:DeleteTable\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:GetTables\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue:GetTable\""
          }), "\n            ],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:glue:us-east-1:${accountId}:catalog\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:glue:us-east-1:${accountId}:database/ht_expedia__glue_database_sync_*\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:glue:us-east-1:${accountId}:table/ht_expedia__glue_database_sync_*/*\""
          }), "\n            ]\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"S3AccessForGlue\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:ListBucket\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:GetObject\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"s3:PutObject\""
          }), "\n            ],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::{bucketName}\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:s3:::${bucketName}/*\""
          }), "\n            ]\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"CleanRoomsAccess\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " [\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:ListConfiguredTables\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:CreateConfiguredTable\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:GetConfiguredTable\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:ListConfiguredTableAssociations\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:CreateConfiguredTableAssociation\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:GetConfiguredTableAssociation\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:GetConfiguredTableAnalysisRule\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:CreateConfiguredTableAnalysisRule\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:GetConfiguredTableAssociationAnalysisRule\""
          }), ",\n                ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms:CreateConfiguredTableAssociationAnalysisRule\""
          }), "\n\t\t\t],\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"*\""
          }), "\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"AllowPassRole\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"iam:PassRole\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Resource\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:iam::${accountId}:role/${IAMRoleName}\""
          }), "\n        }\n    ]\n}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "and Trust policy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: ["{\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Version\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2012-10-17\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Statement\":"
          }), " [\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"AllowUserAssumeRole\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Principal\":"
          }), " {\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"AWS\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"arn:aws:iam::${accountId}:user/${IAMUserName}\""
          }), "\n            },\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sts:AssumeRole\""
          }), "\n        },\n        {\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Sid\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"AllowGlueAndCleanRoomsService\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Effect\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Allow\""
          }), ",\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Principal\":"
          }), " {\n                ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Service\":"
          }), " [\n                    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"cleanrooms.amazonaws.com\""
          }), ",\n                    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"glue.amazonaws.com\""
          }), "\n                ]\n            },\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"Action\":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sts:AssumeRole\""
          }), "\n        }\n    ]\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-aws-dcr--expedia-group-media-solutions",
        children: "Connect to AWS DCR + Expedia Group Media Solutions"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Expedia Group Media Solutions"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Expedia Group Media Solutions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Bucket Name"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "AWS Glue and Data Clean Room IAM Role Arn"
          }), ": For the custom role outlined above, the Role ARN can be found under the custom role's Summary > Arn"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Data Clean Room Collaboration Membership ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Expedia Group Media Solutions destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Expedia Group Media Solutions destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-data",
        children: "Syncing conversion data"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Expedia Group Media Solutions conversion data for analysis in AWS Data Clean Room."
      }), "\n", _jsx(_components.p, {
        children: "Your data will be:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Sent to the specified S3 bucket with the path ", _jsx(_components.code, {
            children: "ht_expedia__dcr/upload.csv"
          }), ". ", _jsx(_components.strong, {
            children: "Do not put any other files in the folder."
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Converted to an S3 table with the name ", _jsx(_components.code, {
            children: "ht_expedia__glue_table_{sync_id}"
          }), " inside the S3 database with the name ", _jsx(_components.code, {
            children: "ht_expedia__glue_database_{sync_id}"
          }), ". ", _jsx(_components.strong, {
            children: "Do not append any other data to the table"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Associated to the entered Data Clean Room collaboration."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to Expedia's AWS Data Clean Room with this schema:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Mapping"
            }), _jsx(_components.th, {
              align: "center",
              children: "Type"
            }), _jsx(_components.th, {
              align: "center",
              children: "Required"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Event ID"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Timestamp (ISO8601)"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Yes"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Transaction value (USD)"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Transaction category"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Email"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Email (SHA256 Hex)"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Phone number"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Phone number (SHA256 Hex)"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "string"
            }), _jsx(_components.td, {
              align: "center",
              children: "No"
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Expedia recommends to share both email and phone numbers (either hashed or unhashed) to increase the match rate of the collaboration."
        })
      }), "\n", _jsx(_components.h4, {
        id: "adding-your-table-to-the-collaboration",
        children: "Adding your table to the collaboration"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch handles associating a table with your data to the Expedia Data Clean Room collaboration. Additional analysis rules should be added to the Hightouch table that was added to the Expedia Data Clean Room collaboration to use your data with Expedia's."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
